import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import usePushNotifications from "@/hooks/usePushNotifications";

/**
 * Ex-dividend push alert preferences.
 *
 * Two knobs surface from `/api/push/status`:
 *   - `ex_div_alerts_enabled` (bool)     — opt-out toggle
 *   - `ex_div_alerts_lead_days` (0..3)   — how many days before the ex-date
 *                                          the daily 08:00 ET job pings the user
 *
 * We don't gate on `subscribed` here — a user can save the preference before
 * they've installed the PWA + granted permission (still shows an inline hint
 * pointing them at the notification-permission button in the header).
 */
const LEAD_OPTIONS = [
  { value: 0, label: "Day of" },
  { value: 1, label: "1 day before" },
  { value: 2, label: "2 days before" },
  { value: 3, label: "3 days before" },
];

export default function ExDivAlertPreferences() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [leadDays, setLeadDays] = useState(1);
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const push = usePushNotifications();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/push/status");
        if (cancelled) return;
        setEnabled(r.data.ex_div_alerts_enabled !== false);
        setLeadDays(Number(r.data.ex_div_alerts_lead_days ?? 1));
        setSubscribed(!!r.data.subscribed);
      } catch {
        // Non-critical — leave defaults.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/push/ex-div-preferences", patch);
      if (patch.enabled !== undefined) setEnabled(!!r.data.ex_div_alerts_enabled);
      if (patch.lead_days !== undefined) setLeadDays(Number(r.data.ex_div_alerts_lead_days));
      toast.success("Alert preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="ex-div-alerts-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {enabled ? (
              <Bell className="w-4 h-4 text-primary shrink-0" />
            ) : (
              <BellOff className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <div className="min-w-0">
              <h3 className="serif text-lg leading-tight">Ex-dividend alerts</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Push notification a set number of days before each ex-div date so you never miss the buy-in window.
              </p>
            </div>
          </div>
          <Switch
            checked={enabled}
            disabled={saving}
            onCheckedChange={(v) => save({ enabled: v })}
            data-testid="ex-div-alerts-toggle"
          />
        </div>

        {enabled && (
          <div className="mt-4 space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Lead time</div>
            <div className="grid grid-cols-4 gap-1 rounded-full border border-border p-0.5" role="tablist">
              {LEAD_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => save({ lead_days: o.value })}
                  disabled={saving}
                  data-testid={`ex-div-lead-${o.value}`}
                  className={`px-2 py-1 rounded-full mono text-[10px] uppercase tracking-widest transition-colors ${
                    leadDays === o.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <p className="mono text-[10px] text-muted-foreground pt-1">
              Sweep runs daily at 08:00 America/New_York. Preview delivery from the ⓘ icon in the header.
            </p>
          </div>
        )}

        {!subscribed && enabled && (
          <div className="mt-3 rounded-md border border-primary/30 bg-primary/[0.04] p-2.5 text-xs text-foreground/80 flex items-center justify-between gap-2">
            <span>Turn on notifications on this device to receive alerts.</span>
            {push.supported && (
              <Button
                size="sm"
                className="rounded-full shrink-0"
                disabled={push.loading}
                onClick={async () => { await push.enable(); const r = await api.get("/push/status").catch(() => null); if (r) setSubscribed(!!r.data.subscribed); }}
                data-testid="ex-div-enable-notifications"
              >
                {push.loading ? "Enabling…" : "Turn on"}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
